import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import * as yup from 'yup';
import { NO_DATA_TEMPLATE } from '../../../constants/strings/contract/form-constants';
import {
  BillingModelType,
  EngagementType,
  PackageOfferingsType,
  PackageType,
} from '../../../types';
import {
  extractMilestones,
  isAdminFeeClaimsModelById,
  isDateGap,
  isDateRangeOverlappingContracts,
  isFeeForServiceModelById,
  isFeeForServicePackageByName,
  isMilestoneModelById,
} from '../../../utils/contract-utils';
import {
  ContractTypeExtension,
  RawInnerPackageFormValues,
  ValidBillingModel,
} from './custom-package-types';

dayjs.extend(isSameOrBefore);

const isContractLocked = (
  editId: number | undefined,
  startDate: Date | null,
): boolean =>
  editId != null &&
  startDate != null &&
  dayjs(startDate).isSameOrBefore(dayjs(), 'day');

export const yupContract = yup.array().defined().of(yup.object());

export const InnerPackageValidationSchema = yup.object().shape({
  id: yup.number().nullable().defined(),
  billingModelId: yup.number().min(1).required('Billing model is required'),
  packageId: yup
    .number()
    .min(1, 'Package is required')
    .required('Package is required')
    .when(
      ['$availablePackages', '$billingModels', 'billingModelId'],
      ([availablePackages, billingModels, billingModelId], schema) =>
        schema.test(
          'acuteOnlyPackage',
          'Acute only package can only be selected with standard billing model',
          value => {
            if (!availablePackages || !billingModels) return true;
            const selectedPackage = availablePackages.find(
              (p: PackageType) => p.id === value,
            );
            if (
              // when all program indications are acute only, only standard billing model is allowed
              selectedPackage?.packageOfferings.length > 0 &&
              selectedPackage?.packageOfferings.every(
                (po: PackageOfferingsType) =>
                  po?.programIndication?.includes('acute'),
              ) &&
              !billingModels.find(
                (bm: BillingModelType) =>
                  bm.name === 'standard' && bm.id === billingModelId,
              )
            ) {
              return false;
            }
            return true;
          },
        ),
    )
    .when(
      ['$availablePackages', '$billingModels', 'billingModelId'],
      ([availablePackages, billingModels, billingModelId], schema) =>
        schema.test(
          'InPersonVisitAndPtvvOnlyFFS',
          'PTVV and In Person Visit packages can only be selected with Fee for Service billing model',
          value => {
            if (!availablePackages || !billingModels) return true;
            const selectedPackage =
              availablePackages.find((p: PackageType) => p.id === value) ?? {};
            if (isFeeForServicePackageByName(selectedPackage?.name ?? '')) {
              return isFeeForServiceModelById(billingModels, billingModelId);
            }
            return true;
          },
        ),
    ),
  price: yup
    .number()
    .min(0, 'Price must be greater than or equal to 0')
    .typeError('Price invalid')
    .required('Price is required')
    .test('2 decimals', 'Price invalid', number =>
      Number.isInteger(number * 10 ** 2),
    )
    .when(
      ['$billingModels', 'billingModelId', 'currentTemplate'],
      ([billingModels, billingModelId, currentTemplate], schema) => {
        if (isMilestoneModelById(billingModels, billingModelId)) {
          return schema.test({
            name: 'milestonePriceCheck',
            skipAbsent: false,
            test(value, ctx) {
              if (
                currentTemplate?.contract?.length === 0 ||
                currentTemplate?.name === NO_DATA_TEMPLATE.name
              )
                return true;

              const milestonePriceTotal = extractMilestones(
                currentTemplate?.contract as ContractTypeExtension[],
              ).reduce((total, { payment }) => total + payment, 0);

              if (milestonePriceTotal !== value) {
                return ctx.createError({
                  message: 'Milestone prices do not match package price',
                });
              }
              return true;
            },
          });
        }
        return schema;
      },
    )
    .test({
      name: 'newPackagePriceUnderLockedCap',
      skipAbsent: true,
      test(value, ctx) {
        if (value == null) return true;
        const { id, billingModelId } = ctx.parent;
        if (id != null) return true;

        const contract = ctx.from?.[1]?.value;
        const contractStart = contract?.startDate;
        if (
          !contractStart ||
          !dayjs(contractStart).isSameOrBefore(dayjs(), 'day')
        ) {
          // Contract not locked — cap auto-sync applies, no ceiling enforced.
          return true;
        }

        const { billingModels, serverUserAnnualCap } =
          ctx?.options?.context ?? {};
        // AFC and FFS packages are carved out of cap invariants (see
        // userAnnualCapEqualToMaxPackagePrice), so don't ceiling them here either.
        if (isAdminFeeClaimsModelById(billingModels, billingModelId)) {
          return true;
        }
        if (isFeeForServiceModelById(billingModels, billingModelId)) {
          return true;
        }
        if (serverUserAnnualCap == null) return true;

        if (value > serverUserAnnualCap) {
          return ctx.createError({
            message: `Price cannot exceed user annual cap (${serverUserAnnualCap}) on a started contract`,
          });
        }
        return true;
      },
    }),
  initialFee: yup
    .number()
    .min(0, 'Initial fee must be greater than or equal to 0')
    .typeError('Initial fee invalid')
    .required('Initial fee is required')
    .test('2 decimals', 'Initial fee invalid', number =>
      Number.isInteger(number * 10 ** 2),
    ),
  activityFee: yup
    .number()
    .min(0, 'Activity fee must be greater than or equal to 0')
    .typeError('Activity fee invalid')
    .required('Activity fee is required')
    .test('2 decimals', 'Activity fee invalid', number =>
      Number.isInteger(number * 10 ** 2),
    ),
  feePercent: yup
    .number()
    .min(0, 'Fee percent must be between 0 and 100')
    .max(100, 'Fee percent must be between 0 and 100')
    .typeError('Fee percent invalid')
    .defined()
    .when(
      ['billingModelId', '$billingModels'],
      ([billingModelId, billingModels], schema) => {
        if (isAdminFeeClaimsModelById(billingModels, billingModelId)) {
          return schema
            .required('Fee percent is required')
            .test('2 decimals', 'Fee percent invalid', (number: number) =>
              Number.isInteger(number * 10 ** 2),
            );
        }
        return schema;
      },
    ),
  selectedBillableActivities: yup
    .object()
    .defined()
    .when(
      ['billingModelId', '$billingModels'],
      ([billingModelId, billingModels], schema) => {
        if (isAdminFeeClaimsModelById(billingModels, billingModelId)) {
          return schema;
        }
        return schema.test(
          'has at least 1',
          'at least 1 billable activity must be selected',
          value => Object.keys(value).length > 0,
        );
      },
    )
    .when(
      ['billingModelId', '$billingModels'],
      ([billingModelId, billingModels], schema) =>
        schema.test(
          'billableActivityOnlyFFS',
          'Billable activities cannot be in this package and a Fee for Service package',
          (value, ctx) => {
            const selectedPackages = ctx.from?.[2]?.value.selectedPackages;
            if (!selectedPackages || selectedPackages.length <= 1) return true;

            const ffsBillingModelId = billingModels.find(
              (bm: BillingModelType) =>
                bm.name === ValidBillingModel.FEE_FOR_SERVICE,
            )?.id;
            if (!ffsBillingModelId || billingModelId === ffsBillingModelId)
              return true;

            const ffsBillableActivities = selectedPackages
              .filter(
                (pkg: RawInnerPackageFormValues) =>
                  pkg.billingModelId === ffsBillingModelId,
              )
              .flatMap((pkg: RawInnerPackageFormValues) =>
                Object.keys(pkg.selectedBillableActivities),
              );

            return !ffsBillableActivities.some((ba: string) =>
              Object.keys(value).includes(ba),
            );
          },
        ),
    ),
  currentTemplate: yup
    .object()
    .shape({
      name: yup.string(),
      contract: yupContract,
      contractTemplateId: yup.number(),
    })
    .typeError('Invalid Template')
    .when(
      ['$billingModels', 'billingModelId'],
      ([billingModels, billingModelId], schema) => {
        // if billing model is milestone, then template is required
        if (isMilestoneModelById(billingModels, billingModelId)) {
          return schema.test(
            'requiredTemplate',
            'Template is required for milestone billing models',
            value => value?.name !== NO_DATA_TEMPLATE.name,
          );
        }
        return schema;
      },
    )
    .when(
      ['$billingModels', '$engagementTypes'],
      ([billingModels, engagementTypes], schema) =>
        schema.test(
          'billableActivityOnlyFFS',
          'Billable activities cannot be in this template and a Fee for Service package',
          (value, ctx) => {
            const selectedPackages = ctx.from?.[2]?.value.selectedPackages;
            if (!selectedPackages || selectedPackages.length <= 1) return true;

            const ffsBillingModelId = billingModels.find(
              (bm: BillingModelType) =>
                bm.name === ValidBillingModel.FEE_FOR_SERVICE,
            )?.id;
            if (!ffsBillingModelId) return true;

            const ffsBillableActivities = selectedPackages
              .filter(
                (pkg: RawInnerPackageFormValues) =>
                  pkg.billingModelId === ffsBillingModelId,
              )
              .flatMap((pkg: RawInnerPackageFormValues) =>
                Object.keys(pkg.selectedBillableActivities).map(Number),
              );

            const contract = value?.contract as ContractTypeExtension[];
            if (!contract) return true;

            const contractBats = new Set(
              contract.flatMap(c => c.billableActivityTypes as string[]),
            );

            const templateEngTypes = engagementTypes
              .filter((et: EngagementType) => contractBats.has(et.name))
              .map((et: EngagementType) => et.id);

            return !ffsBillableActivities.some((ba: number) =>
              templateEngTypes.includes(ba),
            );
          },
        ),
    ),
  costShareRequired: yup.boolean().defined(),
  deductionTiming: yup
    .string()
    .nullable()
    .defined()
    .when(['costShareRequired'], ([costShareRequired], schema) => {
      if (costShareRequired) {
        return schema.test(
          'deductionTimingRequired',
          'Deduction timing is required',
          value => value !== null && value !== '',
        );
      }
      // Reset to null when costShareRequired is false
      return schema.transform(() => null);
    }),
  cadence: yup
    .string()
    .nullable()
    .defined()
    .when(['costShareRequired'], ([costShareRequired], schema) => {
      if (costShareRequired) {
        return schema.test(
          'cadenceRequired',
          'Cadence is required',
          value => value !== null && value !== '',
        );
      }
      // Reset to null when costShareRequired is false
      return schema.transform(() => null);
    }),
  billingTypeOverride: yup.string().nullable().defined(),
  startDate: yup
    .date()
    .typeError('Invalid Date')
    .nullable()
    .required('Package start date is required')
    .test({
      name: 'packageStartDateWithinContract',
      skipAbsent: true,
      test(value, ctx) {
        if (!value) return true;
        // Tolerate legacy existing packages whose start dates fall outside
        // their contract range. A small number of pre-existing contracts
        // (created before this validator) violate the invariant; failing
        // them here would block unrelated edits (e.g. toggling a billable
        // flag) on those contracts. New packages still get the full check.
        const { id } = ctx.parent;
        if (id != null) return true;
        const contract = ctx.from?.[1]?.value;
        const contractStart = contract?.startDate;
        const contractEnd = contract?.endDate;
        // If the contract date range itself is invalid (end before start),
        // skip the package-range check so the contract-level error surfaces.
        if (
          contractStart &&
          contractEnd &&
          dayjs(contractEnd).isBefore(contractStart, 'day')
        ) {
          return true;
        }
        if (contractStart && dayjs(value).isBefore(contractStart, 'day')) {
          return ctx.createError({
            message: 'Package start date must be on or after contract start',
          });
        }
        if (contractEnd && dayjs(value).isAfter(contractEnd, 'day')) {
          return ctx.createError({
            message: 'Package start date must be on or before contract end',
          });
        }
        return true;
      },
    }),
});

export const PackageValidationSchema = yup.object({
  startDate: yup
    .date()
    .typeError('Invalid Date')
    .required('Start date is required')
    .nullable()
    .test({
      name: 'startDateInvalid',
      skipAbsent: false,
      test(value) {
        return dayjs(value).isValid();
      },
    })
    .test({
      name: 'startDatePastDisabled',
      skipAbsent: true,
      test(value, ctx) {
        const { startDateDisablePast } = ctx?.options?.context ?? {};
        if (
          startDateDisablePast &&
          dayjs(value).isBefore(dayjs(), 'day') === true
        ) {
          return ctx.createError({
            message: 'Start date is disabled before today',
          });
        } else return true;
      },
    })
    .test({
      name: 'startDateOverlaps',
      skipAbsent: true,
      test(value, ctx) {
        const { currentContracts, editId } = ctx?.options?.context ?? {};
        const { endDate } = ctx.parent;
        if (currentContracts && currentContracts.length >= 1 && value) {
          const startDateOverlapCheck = isDateRangeOverlappingContracts(
            value,
            endDate,
            currentContracts,
            editId,
          );
          if (startDateOverlapCheck.startDateOverlap) {
            return ctx.createError({
              path: 'startDate',
              message: 'Start date overlaps current contracts',
            });
          }
        }
        return true;
      },
    }),
  endDate: yup
    .date()
    .typeError('Invalid Date')
    .defined()
    .test({
      name: 'endDateInvalid',
      skipAbsent: false,
      test(value) {
        return value === null || dayjs(value).isValid();
      },
    })
    .test({
      name: 'endDateOverlap',
      skipAbsent: false,
      test(value, ctx) {
        const { currentContracts, editId } = ctx?.options?.context ?? {};
        const { startDate } = ctx.parent;
        if (currentContracts && currentContracts.length >= 1) {
          const endDateOverlapCheck = isDateRangeOverlappingContracts(
            startDate,
            value,
            currentContracts,
            editId,
          );
          if (endDateOverlapCheck.endDateOverlap) {
            return ctx.createError({
              message: 'End date overlaps current contracts',
            });
          }
        }
        return true;
      },
    })
    .test({
      name: 'endDateBeforeStart',
      skipAbsent: true,
      test(value, ctx) {
        const { startDate } = ctx.parent;
        if (dayjs(value).isBefore(startDate, 'day')) {
          return ctx.createError({
            message: 'End date is before start date',
          });
        }
        return true;
      },
    })
    .test({
      name: 'endDateGapCheck',
      skipAbsent: false,
      test(value, ctx) {
        const { currentContracts, editId } = ctx?.options?.context ?? {};
        if (currentContracts && currentContracts.length >= 1) {
          const endDateGapCheck = isDateGap(
            dayjs(value).format(),
            editId ?? null,
            currentContracts,
            'end',
          );
          if (endDateGapCheck.isGap) {
            return ctx.createError({
              message: `There is a gap with selected end date and next contract of ${endDateGapCheck.gapLength} days`,
            });
          }
        }
        return true;
      },
    })
    .nullable(),
  userAnnualCap: yup
    .number()
    .min(0, 'User annual cap must be greater than or equal to 0')
    .typeError('Invalid price')
    .required('User annual cap is required')
    .test('2 decimals', 'User annual cap invalid', number =>
      Number.isInteger(number * 10 ** 2),
    ),
  globalDailyEngagementCap: yup
    .number()
    .nullable()
    .defined()
    .transform((value, original) => (original === '' ? null : value))
    .integer('Must be a whole number')
    .min(1, 'Must be between 1 and 31')
    .max(31, 'Must be between 1 and 31'),
  currency: yup
    .object()
    .shape({
      label: yup.string().default('USD $'),
      value: yup.string().default('USD'),
    })
    .defined(),
  isVoid: yup.boolean().defined(),
  partnershipId: yup.number().nullable().defined(),
  billingType: yup.string().nullable().defined(),
  selectedPackages: yup
    .array()
    .min(1)
    .required()
    .of(InnerPackageValidationSchema)
    .test({
      name: 'uniquePackages',
      skipAbsent: false,
      test(value, ctx) {
        if (value.length <= 1) {
          return true;
        }
        const { availablePackages } = ctx?.options?.context ?? {};

        const seenPackageIds = new Set();

        for (const v of value) {
          const packageId = v.packageId;
          const packageName = (availablePackages as PackageType[]).find(
            p => p.id === packageId,
          )?.name;
          if (packageId && seenPackageIds.has(packageId)) {
            return ctx.createError({
              path: 'selectedPackages',
              message: `${packageName} package can only be selected once`,
            });
          }
          if (packageId) {
            seenPackageIds.add(packageId);
          }
        }

        return true;
      },
    })
    .test({
      name: 'uniqueProgramIndications',
      skipAbsent: false,
      test(value, ctx) {
        const { availablePackages } = ctx?.options?.context ?? {};
        if (value.length <= 1) {
          return true;
        }

        const seenProgramIndications = new Map();

        for (const v of value) {
          const currentPackage = (availablePackages as PackageType[]).find(
            p => p.id === v.packageId,
          );
          const currentPackageProgramIndications =
            currentPackage?.packageOfferings.map(po => po.programIndication) ??
            [];

          for (const pi of currentPackageProgramIndications) {
            if (seenProgramIndications.has(pi)) {
              const conflictingPackage = seenProgramIndications.get(pi);
              return ctx.createError({
                path: 'selectedPackages',
                message: `${currentPackage?.name} package and ${conflictingPackage.name} package have duplicate program indications`,
              });
            }
            seenProgramIndications.set(pi, currentPackage);
          }
        }

        return true;
      },
    })
    .test({
      name: 'userAnnualCapEqualToMaxPackagePrice',
      skipAbsent: false,
      test(value, ctx) {
        const { userAnnualCap, startDate } = ctx.parent;
        const { billingModels, editId } = ctx?.options?.context ?? {};
        const capEligiblePackages = value.filter(
          pkg =>
            !isAdminFeeClaimsModelById(billingModels, pkg.billingModelId) &&
            !isFeeForServiceModelById(billingModels, pkg.billingModelId),
        );
        if (capEligiblePackages.length === 0) {
          return true;
        }
        const maxPrice = Math.max(...capEligiblePackages.map(pkg => pkg.price));

        if (!isContractLocked(editId, startDate)) {
          return userAnnualCap === maxPrice
            ? true
            : ctx.createError({
                path: 'userAnnualCap',
                message: `User annual cap must be equal to the highest package price (${maxPrice})`,
              });
        }

        return userAnnualCap >= maxPrice
          ? true
          : ctx.createError({
              path: 'userAnnualCap',
              message: `User annual cap must be at least the highest package price (${maxPrice})`,
            });
      },
    })
    .test({
      name: 'engagement not allow before 8/1/2024',
      skipAbsent: false,
      test(value, ctx) {
        const { startDate } = ctx.parent;
        const { billingModels } = ctx?.options?.context ?? {};
        const engagementBillingModelId = billingModels.find(
          (bm: BillingModelType) => bm.name === 'engagement',
        )?.id;
        if (
          value.some(pkg => pkg.billingModelId === engagementBillingModelId) &&
          dayjs(startDate).isBefore('2024-08-01', 'day')
        ) {
          return ctx.createError({
            path: 'startDate',
            message:
              'Engagement billing model cannot be selected before 8/1/2024',
          });
        }
        return true;
      },
    })
    .test({
      name: 'NoMixingEngagementAndCohortMilestone',
      skipAbsent: false,
      test(value, ctx) {
        const { billingModels } = ctx?.options?.context ?? {};
        const engagementId = billingModels.find(
          (bm: BillingModelType) => bm.name === ValidBillingModel.ENGAGEMENT,
        )?.id;
        const cohortMilestoneId = billingModels.find(
          (bm: BillingModelType) =>
            bm.name === ValidBillingModel.COHORT_MILESTONE,
        )?.id;

        const hasEngagement = value.some(
          cp => cp.billingModelId === engagementId,
        );
        const hasCohortMilestone = value.some(
          cp => cp.billingModelId === cohortMilestoneId,
        );

        if (hasEngagement && hasCohortMilestone) {
          return ctx.createError({
            path: 'selectedPackages',
            message:
              'Cannot have both engagement and cohort milestone packages on a single contract',
          });
        }
        return true;
      },
    })
    .test({
      name: 'AllowOnlyOneCohortMilestonePackage',
      skipAbsent: false,
      test(value, ctx) {
        const { billingModels } = ctx?.options?.context ?? {};
        const cohortMilestoneId = billingModels.find(
          (bm: BillingModelType) =>
            bm.name === ValidBillingModel.COHORT_MILESTONE,
        )?.id;

        if (
          value.filter(cp => cp.billingModelId === cohortMilestoneId).length > 1
        ) {
          return ctx.createError({
            path: 'selectedPackages',
            message:
              'Cannot have more than one cohort milestone package on a single contract',
          });
        }
        return true;
      },
    })
    .test({
      name: 'NoConflictingDailyCaps',
      skipAbsent: false,
      test(value, ctx) {
        if (value.length <= 1) return true;

        const dailyCapsByEngagementType = new Map<string, number>();

        for (const pkg of value) {
          for (const [engagementTypeId, details] of Object.entries(
            pkg.selectedBillableActivities,
          )) {
            const dailyCap = (details as Record<string, number> | null)
              ?.dailyCap;
            if (dailyCap === undefined || dailyCap === null) continue;

            const existing = dailyCapsByEngagementType.get(engagementTypeId);
            if (existing !== undefined && existing !== dailyCap) {
              return ctx.createError({
                path: 'selectedPackages',
                message:
                  'Conflicting daily caps across packages for the same billable activity',
              });
            }
            dailyCapsByEngagementType.set(engagementTypeId, dailyCap);
          }
        }
        return true;
      },
    })
    .test({
      name: 'AllowOnlyOneAdminFeeClaimsPackage',
      skipAbsent: false,
      test(value, ctx) {
        const { billingModels } = ctx?.options?.context ?? {};
        const adminFeeModelId = billingModels?.find(
          (bm: BillingModelType) =>
            bm.name === ValidBillingModel.ADMIN_FEE_CLAIMS,
        )?.id;

        if (
          adminFeeModelId &&
          value.filter(cp => cp.billingModelId === adminFeeModelId).length > 1
        ) {
          return ctx.createError({
            path: 'selectedPackages',
            message:
              'Cannot have more than one admin fee claims package on a single contract',
          });
        }
        return true;
      },
    })
    .test({
      name: 'NoCohortAndIndividualMilestonePackages',
      skipAbsent: false,
      test(value, ctx) {
        const { billingModels } = ctx?.options?.context ?? {};
        const individualMilestoneId = billingModels.find(
          (bm: BillingModelType) =>
            bm.name === ValidBillingModel.INDIVIDUAL_MILESTONE,
        )?.id;
        const cohortMilestoneId = billingModels.find(
          (bm: BillingModelType) =>
            bm.name === ValidBillingModel.COHORT_MILESTONE,
        )?.id;
        const hasIndividual = value.some(
          cp => cp.billingModelId === individualMilestoneId,
        );
        const hasCohort = value.some(
          cp => cp.billingModelId === cohortMilestoneId,
        );

        if (hasIndividual && hasCohort) {
          return ctx.createError({
            path: 'selectedPackages',
            message:
              'Cannot have both individual and cohort milestone packages on a single contract',
          });
        }
        return true;
      },
    }),
});
